import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export const useNavigationUtils = () => {
  const location = useLocation();
  const { currentLanguage } = useLanguage();

  // Map service pages to their correct paths
  const servicePages = [
    { key: 'productVideos', path: 'product-videos' },
    { key: 'socialMediaAds', path: 'social-media-ads' },
    { key: 'aiVideo', path: 'ai-video' },
    { key: 'webdesign', path: 'webdesign' },
    { key: 'seo', path: 'seo' },
    { key: 'googleBusinessProfile', path: 'google-business-profile' }
  ];

  // Industry specific pages
  const industryPages = [
    { key: 'finance', path: 'finance' },
    { key: 'luxury', path: 'luxury' },
    { key: 'tourism', path: 'tourism' },
    { key: 'healthcare', path: 'healthcare' }
  ];

  // Service paths by language
  const servicePaths = {
    de: {
      'product-videos': 'leistungen/produktvideos',
      'social-media-ads': 'leistungen/social-media-ads',
      'ai-video': 'ai-video',
      'webdesign': 'webdesign',
      'seo': 'seo',
      'google-business-profile': 'google-business-profile',
      'why-future-level': 'warum-future-level',
      'contact': 'kontakt',
      'pricing': 'preise',
      // Industry specific paths
      'finance': 'leistungen/ki-videomarketing-finanzsektor',
      'luxury': 'leistungen/ki-videomarketing-luxusmarken',
      'tourism': 'leistungen/ki-videomarketing-tourismus',
      'healthcare': 'leistungen/ki-videomarketing-gesundheit',
      // Comparison page
      'comparison': 'ki-videomarketing-vs-traditionelle-produktion'
    },
    en: {
      'product-videos': 'services/product-videos',
      'social-media-ads': 'services/social-media-ads',
      'ai-video': 'ai-video',
      'webdesign': 'web-design',
      'seo': 'seo',
      'google-business-profile': 'google-business-profile',
      'why-future-level': 'why-future-level',
      'contact': 'contact',
      'pricing': 'pricing',
      // Industry specific paths
      'finance': 'services/ai-video-marketing-finance',
      'luxury': 'services/ai-video-marketing-luxury',
      'tourism': 'services/ai-video-marketing-tourism',
      'healthcare': 'services/ai-video-marketing-healthcare',
      // Comparison page
      'comparison': 'ai-video-marketing-vs-traditional-production'
    },
    fr: {
      'product-videos': 'services/videos-de-produits',
      'social-media-ads': 'services/publicites-medias-sociaux',
      'ai-video': 'video-ia',
      'webdesign': 'conception-web',
      'seo': 'referencement',
      'google-business-profile': 'google-business-profile',
      'why-future-level': 'pourquoi-future-level',
      'contact': 'contact',
      'pricing': 'tarifs',
      // Industry specific paths
      'finance': 'services/video-marketing-ia-finance',
      'luxury': 'services/video-marketing-ia-luxe',
      'tourism': 'services/video-marketing-ia-tourisme',
      'healthcare': 'services/video-marketing-ia-sante',
      // Comparison page
      'comparison': 'video-marketing-ia-vs-production-traditionnelle'
    },
    it: {
      'product-videos': 'servizi/video-di-prodotti',
      'social-media-ads': 'servizi/pubblicita-social-media',
      'ai-video': 'video-ai',
      'webdesign': 'web-design',
      'seo': 'seo',
      'google-business-profile': 'google-business-profile',
      'why-future-level': 'perche-future-level',
      'contact': 'contatto',
      'pricing': 'prezzi',
      // Industry specific paths
      'finance': 'servizi/marketing-video-ia-finanza',
      'luxury': 'servizi/marketing-video-ia-lusso',
      'tourism': 'servizi/marketing-video-ia-turismo',
      'healthcare': 'servizi/marketing-video-ia-sanita',
      // Comparison page
      'comparison': 'marketing-video-ia-vs-produzione-tradizionale'
    }
  };

  // Check if current path includes service path
  const isServiceActive = (path: string) => {
    const currentPath = location.pathname.toLowerCase();
    return servicePaths[currentLanguage as keyof typeof servicePaths][path] && 
           currentPath.includes(servicePaths[currentLanguage as keyof typeof servicePaths][path]);
  };

  // Get the localized path for a service
  const getServicePath = (path: string) => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths][path]}`;
  };

  // Check if pricing page is active
  const isPricingActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const pricingPath = servicePaths[currentLanguage as keyof typeof servicePaths]['pricing'];
    return pricingPath && currentPath.includes(pricingPath);
  };

  // Get the localized path for pricing
  const getPricingPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['pricing']}`;
  };

  // Check if industry page is active
  const isIndustryActive = (industry: string) => {
    const currentPath = location.pathname.toLowerCase();
    const industryPath = servicePaths[currentLanguage as keyof typeof servicePaths][industry];
    return industryPath && currentPath.includes(industryPath);
  };

  // Get the localized path for an industry
  const getIndustryPath = (industry: string) => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths][industry]}`;
  };

  // Check if comparison page is active
  const isComparisonActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const comparisonPath = servicePaths[currentLanguage as keyof typeof servicePaths]['comparison'];
    return comparisonPath && currentPath.includes(comparisonPath);
  };

  // Get the localized path for the comparison page
  const getComparisonPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['comparison']}`;
  };

  // Check if why-future-level page is active
  const isWhyFutureLevelActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const whyFutureLevelPath = servicePaths[currentLanguage as keyof typeof servicePaths]['why-future-level'];
    return currentPath.includes(whyFutureLevelPath);
  };

  // Get the localized path for why-future-level
  const getWhyFutureLevelPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['why-future-level']}`;
  };

  // Check if contact page is active
  const isContactActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const contactPath = servicePaths[currentLanguage as keyof typeof servicePaths]['contact'];
    return currentPath.includes(contactPath);
  };

  // Get the localized path for contact
  const getContactPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['contact']}`;
  };

  // Check if product videos page is active
  const isProductVideosActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const productVideosPath = servicePaths[currentLanguage as keyof typeof servicePaths]['product-videos'];
    return productVideosPath && currentPath.includes(productVideosPath);
  };

  // Get the localized path for product videos
  const getProductVideosPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['product-videos']}`;
  };

  // Check if social media ads page is active
  const isSocialMediaAdsActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const socialMediaAdsPath = servicePaths[currentLanguage as keyof typeof servicePaths]['social-media-ads'];
    return socialMediaAdsPath && currentPath.includes(socialMediaAdsPath);
  };

  // Get the localized path for social media ads
  const getSocialMediaAdsPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['social-media-ads']}`;
  };

  // Check if AI-Video page is active
  const isAIVideoActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const aiVideoPath = servicePaths[currentLanguage as keyof typeof servicePaths]['ai-video'];
    return aiVideoPath && currentPath.includes(aiVideoPath);
  };

  // Get the localized path for AI-Video
  const getAIVideoPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['ai-video']}`;
  };

  // Check if Webdesign page is active
  const isWebdesignActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const webdesignPath = servicePaths[currentLanguage as keyof typeof servicePaths]['webdesign'];
    return webdesignPath && currentPath.includes(webdesignPath);
  };

  // Get the localized path for Webdesign
  const getWebdesignPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['webdesign']}`;
  };

  // Check if SEO page is active
  const isSEOActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const seoPath = servicePaths[currentLanguage as keyof typeof servicePaths]['seo'];
    return seoPath && currentPath.includes(seoPath);
  };

  // Get the localized path for SEO
  const getSEOPath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['seo']}`;
  };

  // Check if Google Business Profile page is active
  const isGoogleBusinessProfileActive = () => {
    const currentPath = location.pathname.toLowerCase();
    const gbpPath = servicePaths[currentLanguage as keyof typeof servicePaths]['google-business-profile'];
    return gbpPath && currentPath.includes(gbpPath);
  };

  // Get the localized path for Google Business Profile
  const getGoogleBusinessProfilePath = () => {
    return `/${currentLanguage}/${servicePaths[currentLanguage as keyof typeof servicePaths]['google-business-profile']}`;
  };

  return {
    servicePages,
    industryPages,
    isServiceActive,
    getServicePath,
    isIndustryActive,
    getIndustryPath,
    isComparisonActive,
    getComparisonPath,
    isWhyFutureLevelActive,
    getWhyFutureLevelPath,
    isContactActive,
    getContactPath,
    isProductVideosActive,
    getProductVideosPath,
    isSocialMediaAdsActive,
    getSocialMediaAdsPath,
    isPricingActive,
    getPricingPath,
    isAIVideoActive,
    getAIVideoPath,
    isWebdesignActive,
    getWebdesignPath,
    isSEOActive,
    getSEOPath,
    isGoogleBusinessProfileActive,
    getGoogleBusinessProfilePath
  };
};
